import { Box } from "@itandi/itandi-bb-ui/Box";
import { ErrorFeedback } from "@itandi/itandi-bb-ui/ErrorFeedback";
import { Grid } from "@itandi/itandi-bb-ui/Grid";
import { Heading } from "@itandi/itandi-bb-ui/Heading";
import { Loader } from "@itandi/itandi-bb-ui/Loader";
import { useRouter } from "next/router";
import { useCallback, useState } from "react";
import { VendorLayoutV2 } from "~/components/common/layouts/VendorLayoutV2";
import { DealSearch } from "~/components/specifics/vendors/deals/index/search/DealSearch";
import { MEDIA_QUERY, useMediaQuery } from "~/hooks/useMediaQuery";
import { useDealCompanySettings } from "~/hooks/vendors/useDealCompanySettings";
import { useDeals } from "~/hooks/vendors/useDeals";
import { useStatuses } from "~/hooks/vendors/useStatuses";
import type { DealSearchCondition } from "~/models/Deal";
import { PATH } from "~/pages/vendors/deals";
import {
	buildSearchConditionByParams,
	convertQueryStringUrl
} from "~/utils/DealSearchConditionUtils";
import { DealTable } from "./table/DealTable";

export function Page() {
	const router = useRouter();
	const { match: isSp, isLoaded: windowLoaded } = useMediaQuery(MEDIA_QUERY.sp);
	const [dealSearchCondition, setDealSearchCondition] =
		useState<DealSearchCondition>(
			buildSearchConditionByParams(router.query, true)
		);
	// isLoading中用の総ページ数State
	const [totalPage, setTotalPage] = useState<number>(1);
	const { data: statuses, isLoading: isStatusLoading } = useStatuses().index();
	const {
		data: paginateDeal,
		mutate: mutatePaginateDeal,
		isLoading: isDealLoading,
		error: paginateDealError
	} = useDeals().index(dealSearchCondition);
	const { data: maintenanceCompanies, isLoading: isMaintenanceCompanyLoading } =
		useDealCompanySettings().index("");
	const handleChangeCondition = useCallback(
		async (newCondition: DealSearchCondition): Promise<void> => {
			setDealSearchCondition(newCondition);
			const newUrl = convertQueryStringUrl(PATH, newCondition);
			await router.replace(newUrl);
			setTotalPage(paginateDeal?.sumPageCount || totalPage);
			await mutatePaginateDeal?.().then();
		},
		[mutatePaginateDeal, paginateDeal, totalPage, router]
	);
	if (!windowLoaded) {
		return null;
	}
	const statusNames =
		statuses === null
			? {}
			: Object.fromEntries(statuses.map((status) => [status.id, status.name]));
	return (
		<VendorLayoutV2>
			<Loader isLoading={isDealLoading || isStatusLoading} position="screen" />
			<Box marginTop={8} marginX={16}>
				<Grid gap={8}>
					<Heading as="h1">案件一覧</Heading>
					{maintenanceCompanies !== null && !isMaintenanceCompanyLoading && (
						<DealSearch
							dealSearchCondition={dealSearchCondition}
							handleChangeCondition={handleChangeCondition}
							isSp={isSp}
							maintenanceCompanies={maintenanceCompanies}
						/>
					)}
					{paginateDeal !== null && (
						<DealTable
							dealSearchCondition={dealSearchCondition}
							deals={paginateDeal.objects}
							handleChangeCondition={handleChangeCondition}
							isSp={isSp}
							statusNames={statusNames}
							total={paginateDeal.count}
						/>
					)}
					{paginateDealError?.response ? (
						<ErrorFeedback>
							{paginateDealError.response.data.message}
						</ErrorFeedback>
					) : null}
				</Grid>
			</Box>
		</VendorLayoutV2>
	);
}
